import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useDragResize — minimal headless hook for a fixed-positioned, draggable + resizable panel.
 *
 * Usage:
 *   const { x, y, w, h, onDragMouseDown, onResizeMouseDown } = useDragResize({
 *     initial: { x: window.innerWidth - 420, y: 80, w: 380, h: 560 },
 *     minW: 280, minH: 220,
 *     storageKey: "co_panel_review_item",  // optional, persists across reloads
 *   });
 *
 *   <aside style={{ position:"fixed", left:x, top:y, width:w, height:h }}>
 *     <header onMouseDown={onDragMouseDown}>drag me</header>
 *     <div className="resize-handle" onMouseDown={onResizeMouseDown} />
 *   </aside>
 */
export function useDragResize({ initial, minW = 280, minH = 220, storageKey = null }) {
  const [box, setBox] = useState(() => {
    if (storageKey && typeof window !== "undefined") {
      try {
        const v = JSON.parse(localStorage.getItem(storageKey) || "null");
        if (v && typeof v.x === "number") return v;
      } catch { /* ignore */ }
    }
    return initial;
  });
  const dragStart = useRef(null);
  const resizeStart = useRef(null);

  // Clamp to viewport
  const clamp = useCallback((b) => {
    if (typeof window === "undefined") return b;
    const maxX = Math.max(0, window.innerWidth - 80);
    const maxY = Math.max(0, window.innerHeight - 60);
    return {
      x: Math.min(Math.max(0, b.x), maxX),
      y: Math.min(Math.max(0, b.y), maxY),
      w: Math.max(minW, Math.min(b.w, window.innerWidth)),
      h: Math.max(minH, Math.min(b.h, window.innerHeight)),
    };
  }, [minW, minH]);

  useEffect(() => {
    if (!storageKey) return;
    try { localStorage.setItem(storageKey, JSON.stringify(box)); } catch { /* ignore */ }
  }, [box, storageKey]);

  const onDragMouseDown = useCallback((e) => {
    e.preventDefault();
    dragStart.current = { startX: e.clientX, startY: e.clientY, baseX: box.x, baseY: box.y };
    const onMove = (ev) => {
      const s = dragStart.current; if (!s) return;
      setBox((b) => clamp({ ...b, x: s.baseX + (ev.clientX - s.startX), y: s.baseY + (ev.clientY - s.startY) }));
    };
    const onUp = () => {
      dragStart.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [box.x, box.y, clamp]);

  const onResizeMouseDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    resizeStart.current = { startX: e.clientX, startY: e.clientY, baseW: box.w, baseH: box.h };
    const onMove = (ev) => {
      const s = resizeStart.current; if (!s) return;
      setBox((b) => clamp({ ...b, w: s.baseW + (ev.clientX - s.startX), h: s.baseH + (ev.clientY - s.startY) }));
    };
    const onUp = () => {
      resizeStart.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [box.w, box.h, clamp]);

  return { ...box, onDragMouseDown, onResizeMouseDown, setBox };
}
